<template>
  <card-page card-title="User Details" :class="readonly ? 'readonly-form' : ''">
    <vue-element-loading :active="busy"></vue-element-loading>
    <vue-form-generator
      ref="formUserDetails"
      :model="value"
      :options="formOptions"
      :schema="schemaUserDetails"
    />
    <additive-widget
      v-if="showApplicationRolesForm"
      ref="formApplicationRoles"
      v-model="value"
      :form-options="formOptions"
      :schema="schemaApplicationRoles"
    />
  </card-page>
</template>

<script>
import {
  selectOptions,
  arrayOptions
} from '@/components/form/FormElementOption';
import CardPage from '../../../components/Views/CardPage';
import FormModelMixin from '../../../../components/mixins/FormModelMixin';
import FormFieldValidationMixin from '../../../../components/mixins/FormFieldValidationMixin';
import AdditiveWidget from '../../../../components/form/fields/AdditiveWidget';

export default {
  name: 'UserProfileCard',
  components: {
    CardPage,
    AdditiveWidget
  },
  props: {
    value: {
      type: Object
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  mixins: [FormModelMixin, FormFieldValidationMixin],
  data() {
    return {
      model: {
        isUser: this.isUser,
        applications: []
      },
      formReferences: ['formUserDetails'],
      showApplicationRolesForm: false,
      schemaUserDetails: {
        groups: [
          {
            styleClasses: 'w-full mt-0',
            legend: 'General',
            fields: [
              {
                disabled: () => this.readonly || this.hasExternalRecord || this.isInternalUser,
                type: 'vsCheckbox',
                styleClasses: 'vx-col w-full',
                label: 'Make this Contact a User?',
                model: 'isUser',
                onChanged: (model) => {
                  if (!model.isUser) {
                    model.EmailAddress = null;
                    model.connection = null;
                  }
                },
              },
              {
                disabled: () => this.readonly || !this.showConnection,
                visible: model => model && model.isUser === true,
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Username',
                model: 'EmailAddress',
                required: true,
                validator: ['required', 'email']
              },
              {
                disabled: () => this.readonly,
                visible: model =>
                  this.showConnection && model && model.isUser === true,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Connection Type',
                model: 'connection',
                values: () => this.connectionOptions,
                selectOptions,
                required: () => !this.readonly,
                validator: 'required'
              }
            ]
          }
        ]
      },
      schemaApplicationRoles: {
        groups: [
          {
            styleClasses: 'w-full mt-0 flex flex-wrap',
            legend: 'Application Roles',
            fields: [
              {
                model: 'applications',
                ...arrayOptions,
                hideAddButton: true,
                showRemoveButton: false,
                items: {
                  type: 'object',
                  styleClasses: 'w-full',
                  schema: {
                    groups: [
                      {
                        styleClasses: 'w-full',
                        fields: [
                          {
                            type: 'label',
                            styleClasses: 'w-1/3',
                            get: model => model.ApplicationName
                          },
                          {
                            disabled: () => this.readonly,
                            type: 'vueMultiSelect',
                            inputType: 'text',
                            styleClasses: 'w-1/3',
                            model: 'SelectedRoleId',
                            values: model =>
                              model.roles.map(({ RoleName, Role_PK }) => ({
                                name: RoleName,
                                id: Role_PK
                              })),
                            selectOptions
                          },
                          {
                            type: 'label',
                            styleClasses: 'w-1/3'
                          }
                        ]
                      }
                    ]
                  }
                }
              }
            ]
          }
        ]
      },
      formOptions: {
        validateAfterLoad: false,
        validateAfterChanged: true
      },
      connectionOptions: [],
      applications: null,
    };
  },
  watch: {
    'model.isUser': {
      handler(value) {
        if (!this.model || !this.model.applications) {
          this.showApplicationRolesForm = false;
          return;
        }

        this.showApplicationRolesForm = value;
      },
      immediate: true
    },
    'model.IsInternalUser': {
      handler(value) {
        if (value) {
          this.model.isUser = true;
        }
      },
      immediate: true
    },
    applications: {
      handler(value) {
        if (value) {
          this.model.applications = value;

          if (!this.model || !this.model.applications) {
            this.showApplicationRolesForm = false;
            return;
          }

          this.showApplicationRolesForm = this.model.isUser;
        }
      }
    }
  },
  computed: {
    /**
     * Show based on if they are an internal user
     * Or they have not been added yet
     * @returns {*|boolean}
     */
    showConnection() {
      // This means we are on the create screen
      if (this.model && !this.model.id) {
        return true;
      }

      if (this.isInternalUser) {
        return false;
      }

      return this.isUser && !this.hasExternalRecord;
    },
    hasExternalRecord() {
      return this.model && this.model.ExternalIdentity_Key;
    },
    isInternalUser() {
      return this.model && this.model.IsInternalUser === true;
    },
    isUser() {
      return this.model.isUser === true || this.isInternalUser;
    }
  },
  methods: {
    async getConnectionOptions() {
      try {
        const { data } = await this.$api.auth.connections();
        this.connectionOptions = data.data
          .filter(
            item =>
              item.name === 'Username-Password-Authentication' ||
              item.name.includes('AzureAd')
          )
          .map(({ id, name }) => {
            if (name === 'Username-Password-Authentication') {
              return { name: 'Externals', id: name };
            }
            if (name.includes('AzureAd')) {
              return { name: 'Employees', id: name };
            }
          });
      } catch (e) {
        console.log(e);
      }
    },
    async setApplications() {
      await this.getApplications();
    },
    async getApplications() {
      try {
        const { data } = await this.$api.applications.list();
        const applications = data.data;

        if (this.model && this.model.roles) {
          applications.forEach(application => {
            application.roles.forEach(role => {
              this.model.roles.forEach(externalRole => {
                if (
                  role.ExternalIdentity_Key ===
                  externalRole.ExternalIdentity_Key
                ) {
                  application.SelectedRoleId = {
                    id: parseInt(role.Role_PK),
                    name: role.RoleName
                  };
                }
              });
            });
          });
        }

        this.applications = applications;
      } catch (e) {
        console.log(e);
        this.$vs.notify({
          title: 'Error occurred',
          text: 'Not able to retrieve list of applications. Please try again.',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger',
          position: 'top-right'
        });
      }
    }
  },
  async created() {
    await this.getConnectionOptions();
    await this.setApplications();
  }
};
</script>
